import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CommonLayout from './CommonLayout';
import moment from 'moment';
import ButtonSecondary from 'shared/Button/ButtonSecondary';
import ButtonPrimary from 'shared/Button/ButtonPrimary';
import Badge from 'shared/Badge/Badge';
import { BadgeOrder } from '../../components/OrderStatus/BadgeOrder';
import QuillRenderer from '../../@core/components/QuillRenderer';
import ServiceRating from '../../components/ServiceRating/ServiceRating';
import { useUserSessionStore } from '../../stores/UserSession.store';
import { OrderStatusEnum } from '../../enums/OrderStatus.enum';
import OrderChat from '../../components/OrderChat/OrderChat';
import TabButton from '../../components/TabButton';
import { useReservations } from '../../hooks/collections/useReservations';
import { IReservation } from '../../@core/interfaces/IReservation';

enum TabEnum {
  GENERAL = 'general',
  CHAT = 'chat',
  REVIEW = 'review',
}

const ReservationDetailPage = () => {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState<IReservation>(null);
  const [isReservationPast, setIsReservationPast] = useState(false);
  const [activeTab, setActiveTab] = useState<TabEnum>(TabEnum.GENERAL);
  const { getReservationById, updateReservation } = useReservations({ loadData: false })
  const { user } = useUserSessionStore();
  const [companyResponse, setCompanyResponse] = useState<string>();
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  const [isEditingResponse, setIsEditingResponse] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const res = await getReservationById(reservationId);
        if (res) {
          setReservation(res);
          setCompanyResponse(res.companyResponse || '');

          const reservationDateTime = moment(
            `${res.day} ${res.hour}`,
            'YYYY-MM-DD HH:mm'
          );
          const now = moment();
          setIsReservationPast(reservationDateTime.isBefore(now));

          return;
        }
      } catch (error) {
        console.error('Error fetching order:', error);
      } finally {
        setLoading(false);
      }
    };

    if (reservationId) fetchOrder();
  }, [reservationId]);

  const renderOrderHeader = () => {
    if (!reservation) return null;

    return (
      <div className="listingSection__wrap !space-y-6">
        <div className="flex justify-between items-center">
          <Badge name={`Order #${reservation?.orderId}`}/>
          <div><BadgeOrder status={reservation.paymentStatus}/></div>
        </div>

        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
          {reservation.service.title}
        </h2>

        <div className="flex items-center space-x-4">
          <span>
            <i className="las la-calendar-alt"></i>
            <span className="ml-1">Reservation for: </span>
              <span key={reservation.id}>
                {moment(reservation.date).format('MMMM D, YYYY h:mm A')}
              </span>
          </span>
        </div>

        <div className="w-full border-b border-neutral-200 dark:border-neutral-700"/>
      </div>
    );
  };

  const renderOrderDetails = () => {
    if (!reservation) return null;

    return (
      <div className="listingSection__wrap">
        <h2 className="text-2xl font-semibold">Reservation Details</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mb-4"></div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Service Information</h3>
              <p className="text-neutral-500 dark:text-neutral-400">{reservation.service.title}</p>
              {reservation.service.description && (
                <div className="mt-2">
                  <QuillRenderer
                    content={reservation.service.description}
                    className="text-neutral-500 dark:text-neutral-400"
                  />
                </div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-medium">Total</h3>
              <p className="text-neutral-500 dark:text-neutral-400">${reservation.service.price}</p>
            </div>

            <div>
              <h3 className="text-lg font-medium">Order placed on</h3>
              <p className="text-neutral-500 dark:text-neutral-400">
                {moment(reservation.createdAt).format('MMMM D, YYYY')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Location</h3>
              <p className="text-neutral-500 dark:text-neutral-400">
                {reservation.service.details.location.address}
              </p>
            </div>

            {reservation.createdBy && (
              <div>
                <h3 className="text-lg font-medium">Customer</h3>
                <p className="text-neutral-500 dark:text-neutral-400">
                  {reservation.createdBy.name}<br/>
                  {reservation.createdBy.email}<br/>
                  {reservation.createdBy.phone}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderOrderChat = () => {
    if (!reservation) return null;
    const isCreator = user.id === reservation.createdBy.id;
    const isCompany = reservation.service.company && user.id === reservation.service.company.id;
    const isProvider = reservation.service.details.provider && user.id === reservation.service.details.provider.id;

    if (!isCreator && !isCompany && !isProvider) return null;

    return (
      <div className="listingSection__wrap">
        <h2 className="text-2xl font-semibold">Reservation Chat</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mb-4"></div>
        <div className="h-96">
          <OrderChat reservationId={reservation.id}/>
        </div>
      </div>
    );
  }

  const renderServiceRating = () => {
    if (!reservation || !isReservationPast) return null;
    if (reservation.paymentStatus !== OrderStatusEnum.PAID) return null;
    const isCreator = reservation.createdBy && user && reservation.createdBy.id === user.id;
    const isCompanyUser = user && (
      (reservation.service.company && reservation.service.company.id === user.id) ||
      (reservation.service.details.provider && reservation.service.details.provider.id === user.id)
    );
    if (!reservation.rating && !isCreator && !isCompanyUser) return null;

    const handleCompanyResponseSubmit = async () => {
      if (!companyResponse.trim()) return;
      setIsSubmittingResponse(true);

      try {
        await updateReservation(reservation.id, { companyResponse: companyResponse });

        if (reservationId) {
          const updatedReservation = await getReservationById(reservationId);
          if (updatedReservation) setReservation(updatedReservation);
        }

        setIsEditingResponse(false);
      } catch (error) {
        console.error('Error submitting company response:', error);
      } finally {
        setIsSubmittingResponse(false);
      }
    };

    return (
      <div className="listingSection__wrap">
        <h2 className="text-2xl font-semibold">{isCreator ? 'Rate this Service' : 'Service Rating'}</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mb-4"></div>

        {reservation.rating ? (
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-2xl shadow-sm p-6">
            <div className="flex items-center mb-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`text-2xl ${
                    star <= reservation.rating
                      ? 'text-yellow-400'
                      : 'text-gray-300 dark:text-gray-600'
                  }`}
                >
                  ★
                </span>
              ))}
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                {reservation.rating} stars
              </span>
            </div>
            {reservation.comment && (
              <div className="mt-3">
                <p className="text-neutral-500 dark:text-neutral-400">{reservation.comment}</p>
              </div>
            )}

            {reservation.companyResponse && (
              <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <div className="flex justify-between items-center">
                  <h4 className="text-md font-medium mb-1">Company Response</h4>
                  {isCompanyUser && !isEditingResponse && (
                    <ButtonPrimary
                      onClick={() => setIsEditingResponse(true)}
                      className="text-sm px-3 py-1"
                    >
                      <i className="las la-edit mr-1"></i>
                      Edit
                    </ButtonPrimary>
                  )}
                </div>
                {!isEditingResponse && (
                  <p className="text-neutral-500 dark:text-neutral-400">{reservation.companyResponse}</p>
                )}

                {isCompanyUser && isEditingResponse && (
                  <div className="mt-2">
                    <textarea
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:text-white"
                      placeholder="Write your response to this review..."
                      value={companyResponse}
                      onChange={(e) => setCompanyResponse(e.target.value)}
                    ></textarea>
                    <div className="flex space-x-2 mt-2">
                      <ButtonPrimary
                        onClick={handleCompanyResponseSubmit}
                        disabled={isSubmittingResponse}
                      >
                        {isSubmittingResponse ? 'Submitting...' : 'Update Response'}
                      </ButtonPrimary>
                      <ButtonSecondary
                        onClick={() => {
                          setIsEditingResponse(false);
                          setCompanyResponse(reservation.companyResponse || '');
                        }}
                      >
                        Cancel
                      </ButtonSecondary>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isCompanyUser && !reservation.companyResponse && (
              <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
                <h4 className="text-md font-medium mb-1">Respond to this Review</h4>
                <textarea
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-neutral-800 dark:text-white"
                  placeholder="Write your response to this review..."
                  value={companyResponse}
                  onChange={(e) => setCompanyResponse(e.target.value)}
                ></textarea>
                <ButtonPrimary
                  onClick={handleCompanyResponseSubmit}
                  disabled={isSubmittingResponse}
                  className="mt-2"
                >
                  {isSubmittingResponse ? 'Submitting...' : 'Submit Response'}
                </ButtonPrimary>
              </div>
            )}
          </div>
        ) : (
          <ServiceRating
            reservationId={reservation.id}
            initialRating={reservation.rating}
            initialComment={reservation.comment}
            createdBy={reservation.createdBy}
            onRatingSubmitted={() => {
              if (reservationId) {
                getReservationById(reservationId).then(updatedReservation => {
                  if (updatedReservation) {
                    setReservation(updatedReservation);
                  }
                });
              }
            }}
          />
        )}
      </div>
    );
  };

  const renderTabNavigation = () => {
    if (!reservation) return null;

    const isCreator = user.id === reservation.createdBy.id;
    const isCompany = reservation.service.company && user.id === reservation.service.company.id;
    const isProvider = reservation.service.details.provider && user.id === reservation.service.details.provider.id;
    const isChatAvailable = isCreator || isCompany || isProvider;
    const isReviewAvailable = isReservationPast && reservation.paymentStatus === OrderStatusEnum.PAID;

    return (
      <div className="flex border-b border-neutral-200 dark:border-neutral-700 mb-8">
        <TabButton
          label="General"
          isActive={activeTab === TabEnum.GENERAL}
          onClick={() => setActiveTab(TabEnum.GENERAL)}
        />

        {isChatAvailable && (
          <TabButton
            label="Chat"
            isActive={activeTab === TabEnum.CHAT}
            onClick={() => setActiveTab(TabEnum.CHAT)}
          />
        )}

        <TabButton
          label="Review"
          isActive={activeTab === TabEnum.REVIEW}
          onClick={() => isReviewAvailable && setActiveTab(TabEnum.REVIEW)}
          isDisabled={!isReviewAvailable}
          isAvailable={isReviewAvailable}
          title={!isReviewAvailable ? 'Review will be available after the reservation date' : ''}
        />
      </div>
    );
  };

  const renderActionButtons = () => {
    return (
      <div className="listingSection__wrap">
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <ButtonSecondary onClick={() => navigate(-1)}>
            <i className="las la-arrow-left mr-2"></i>
            Back to Reservations
          </ButtonSecondary>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <CommonLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </CommonLayout>
    );
  }

  if (!reservation) {
    return (
      <CommonLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <h2 className="text-2xl font-semibold text-neutral-700 dark:text-neutral-200">Order not found</h2>
          <p className="text-neutral-500 dark:text-neutral-400 mt-2">The order you're looking for doesn't exist or you
            don't have permission to view it.</p>
          <ButtonSecondary className="mt-4" onClick={() => navigate(-1)}>
            Back to Orders
          </ButtonSecondary>
        </div>
      </CommonLayout>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderOrderDetails()
      case 'chat':
        return renderOrderChat()
      case 'review':
        return renderServiceRating()
      default:
        return null;
    }
  };

  return (
    <div>
      <CommonLayout>
        <div className="space-y-8">
          {renderOrderHeader()}
          {renderTabNavigation()}
          {renderTabContent()}
          {renderActionButtons()}
        </div>
      </CommonLayout>
    </div>
  );
};

export default ReservationDetailPage;
